import { Button } from '@daily/shared/components/Button';
import { CloseIcon } from '@daily/shared/components/Icons/CloseIcon';
import { ScreenshareIcon } from '@daily/shared/components/Icons/ScreenshareIcon';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useParticipantProperty, useScreenShare } from '@daily-co/daily-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useScreens } from '../../hooks/useScreens';
import { robotsClassName } from '../../lib/robots';

interface Props {
  onClick(): void;
}

export const ScreenshareButton: React.FC<React.PropsWithChildren<Props>> = ({
  onClick,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isSharingScreen } = useScreenShare();
  const screens = useScreens();

  const firstScreenUserName = useParticipantProperty(
    screens[0]?.session_id,
    'user_name'
  );

  const infoText = useMemo(
    () =>
      isSharingScreen
        ? t('screenShare.stopSharing')
        : screens.length > 1
        ? t('screenShare.peopleWithCount', {
            count: screens.length,
          })
        : t('screenShare.personWithName', {
            name: firstScreenUserName || t('people.someone'),
          }),
    [firstScreenUserName, isSharingScreen, screens, t]
  );

  if (!screens.length) return null;

  return (
    <div>
      <Stack align="center" horizontal>
        <Text>{infoText}</Text>
        {isSharingScreen ? (
          <Button
            id="topbar-screen-share-stop"
            className={robotsClassName('btn-screenshare-stop')}
            onClick={onClick}
            variant="danger"
          >
            <CloseIcon size={16} color={colors.system.white} variant="thick" />
            <VisuallyHidden>{t('screenShare.stopSharing')}</VisuallyHidden>
          </Button>
        ) : (
          <div className="screenShareIcon" role="presentation">
            <ScreenshareIcon size={16} color={colors.baseText} />
          </div>
        )}
      </Stack>
      <style jsx>{`
        div :global(button) {
          border-radius: 4px !important;
          padding: 3px !important;
        }
        .screenShareIcon {
          background-color: ${colors.backgroundAccent};
          border-radius: 4px;
          padding: 4px;
        }
      `}</style>
    </div>
  );
};
